// Firmayı pasife alma / tekrar aktif etme penceresi (açık talepler seçilerek birlikte yönetilir)
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { PassiveHistoryList } from './PassiveHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { aktif: 'Onaylandı', onay_bekliyor: 'Onay Bekliyor', isg_inceleme: 'İSG İncelemesinde' };

const PermitPicker = ({ permits, selected, setSelected }) => (
  <div className="max-h-56 overflow-y-auto rounded-lg border divide-y" data-testid="passive-permit-list">
    {permits.map(p => (
      <label key={p.id} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
        <Checkbox
          checked={selected.includes(p.id)}
          onCheckedChange={(v) => setSelected(prev => (v ? [...prev, p.id] : prev.filter(x => x !== p.id)))}
          data-testid={`passive-permit-check-${p.id}`}
        />
        <div className="text-sm min-w-0">
          <p className="font-medium">Talep No: {p.request_number || '-'} <span className="text-xs font-normal text-slate-500">· {STATUS_LABEL[p.status] || p.status}</span></p>
          <p className="text-xs text-slate-500 truncate">{p.job_description || '-'} · {p.start_date} – {p.end_date}</p>
        </div>
      </label>
    ))}
  </div>
);

export const CompanyPassiveDialog = ({ company, onClose, onDone }) => {
  const activating = !!company?.is_passive;
  const [permits, setPermits] = useState(null);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!company) return;
    setPermits(null); setReason(''); setUntil('');
    axios.get(`${API}/companies/${company.id}/${activating ? 'passive-permits' : 'passive-candidates'}`)
      .then(res => { setPermits(res.data); setSelected(res.data.map(p => p.id)); })
      .catch(() => setPermits([]));
  }, [company, activating]);

  const submit = async (permitIds) => {
    if (!activating && !reason.trim()) { toast.error('Pasife alma açıklaması zorunludur'); return; }
    setSaving(true);
    try {
      const res = activating
        ? await axios.put(`${API}/companies/${company.id}/activate`, { permit_ids: permitIds })
        : await axios.put(`${API}/companies/${company.id}/passive`, { reason: reason.trim(), permit_ids: permitIds, until: until || null });
      toast.success(activating ? `Firma aktif edildi${res.data.activated_permits ? ` (${res.data.activated_permits} talep aktif)` : ''}` : `Firma pasife alındı${res.data.passive_permits ? ` (${res.data.passive_permits} talep pasif)` : ''}`);
      onDone();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'İşlem başarısız');
    } finally { setSaving(false); }
  };

  const hasPermits = permits?.length > 0;

  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="company-passive-dialog">
        <DialogHeader>
          <DialogTitle>{activating ? 'Firmayı Aktif Et' : 'Firmayı Pasife Al'} — {company?.name}</DialogTitle>
          <DialogDescription>
            {activating ? 'Firma tekrar sisteme erişebilecek.' : 'Firma giriş yaptığında sadece pasif uyarısını ve açıklamanızı görür; başka işlem yapamaz.'}
          </DialogDescription>
        </DialogHeader>

        {!activating && (
          <div className="space-y-1">
            <label className="text-sm font-medium">Açıklama *</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Firmanın neden pasife alındığını yazın (firma ekranında görünecek)" data-testid="passive-reason-input" />
            <label className="text-sm font-medium block pt-2">Bitiş tarihi</label>
            <div className="flex items-center gap-2">
              <input
                type="date" value={until} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                onChange={(e) => setUntil(e.target.value)}
                className="h-9 rounded-md border border-slate-200 px-2 text-sm"
                data-testid="passive-until-input"
              />
              {until && <button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => setUntil('')} data-testid="passive-until-clear">Temizle</button>}
            </div>
            <p className="text-xs text-slate-500" data-testid="passive-until-hint">
              {until ? 'Bu tarih geldiğinde firma ve birlikte pasife alınan talepleri otomatik aktif olur.' : 'Boş bırakılırsa pasiflik süresizdir.'}
            </p>
          </div>
        )}
        {activating && company?.passive_until && (
          <p className="text-xs text-slate-500">Planlanan otomatik aktifleşme: {company.passive_until.split('-').reverse().join('.')}</p>
        )}

        {permits === null && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
        {hasPermits && (
          <div className="space-y-2">
            <div className={`flex gap-2 rounded-lg p-3 text-sm ${activating ? 'bg-sky-50 text-sky-800' : 'bg-amber-50 text-amber-800'}`} data-testid="passive-permit-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {activating
                  ? `Bu firmanın pasife alınmış ${permits.length} talebi var. Tekrar aktif olacak talepleri işaretleyin; işaretlenmeyenler pasif kalır.`
                  : `Bu firmanın ${permits.length} açık/onaylı talebi var. Firma pasife alınırken işaretlediğiniz talepler de pasife alınır; işaretlenmeyenler olduğu gibi kalır.`}
              </span>
            </div>
            <PermitPicker permits={permits} selected={selected} setSelected={setSelected} />
          </div>
        )}

        {company && (
          <details className="rounded-lg border px-3 py-2" data-testid="passive-dialog-history">
            <summary className="text-xs font-medium text-slate-600 cursor-pointer">Pasiflik Geçmişi</summary>
            <div className="pt-2"><PassiveHistoryList companyId={company.id} compact /></div>
          </details>
        )}

        <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
          <Button variant="ghost" onClick={onClose} data-testid="passive-cancel-btn">Vazgeç</Button>
          {activating ? (
            <Button onClick={() => submit(selected)} disabled={saving || permits === null} className="bg-emerald-600 hover:bg-emerald-700" data-testid="activate-confirm-btn">
              {hasPermits ? `Firmayı ve Seçili Talepleri (${selected.length}) Aktif Et` : 'Firmayı Aktif Et'}
            </Button>
          ) : (
            <>
              {hasPermits && (
                <Button variant="outline" onClick={() => submit([])} disabled={saving} data-testid="passive-company-only-btn">
                  Talepleri Bırak, Sadece Firmayı Pasife Al
                </Button>
              )}
              <Button onClick={() => submit(hasPermits ? selected : [])} disabled={saving || permits === null || (hasPermits && selected.length === 0)} className="bg-rose-600 hover:bg-rose-700" data-testid="passive-confirm-btn">
                {hasPermits ? `Firmayı ve Seçili Talepleri (${selected.length}) Pasife Al` : 'Firmayı Pasife Al'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Pasif firmanın bitiş tarihini yeniden pasife almadan değiştirir
export const passiveDaysLabel = (until) => {
  if (!until) return 'Süresiz';
  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
  today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(`${until}T00:00:00`) - today) / 86400000);
  if (days <= 0) return 'Bugün aktif olacak';
  if (days === 1) return 'Yarın aktif olacak';
  return `${days} gün kaldı`;
};

export const PassiveUntilDialog = ({ company, onClose, onDone }) => {
  const [until, setUntil] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setUntil(company?.passive_until || ''); }, [company]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/companies/${company.id}/passive-until`, { until: until || null });
      toast.success(res.data.message);
      onDone();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="passive-until-dialog">
        <DialogHeader>
          <DialogTitle>Pasiflik Süresini Değiştir — {company?.name}</DialogTitle>
          <DialogDescription>
            Mevcut süre: {company?.passive_until ? `${company.passive_until.split('-').reverse().join('.')} (${passiveDaysLabel(company.passive_until)})` : 'Süresiz'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium">Yeni bitiş tarihi</label>
          <div className="flex items-center gap-2">
            <input
              type="date" value={until} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} onChange={(e) => setUntil(e.target.value)}
              className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="passive-until-change-input"
            />
            {until && <button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => setUntil('')} data-testid="passive-until-change-clear">Süresiz yap</button>}
          </div>
          <p className="text-xs text-slate-500">{until ? 'Bu tarih geldiğinde firma otomatik aktif olur. Firmaya bilgilendirme e-postası gider.' : 'Süresiz: firma siz aktif edene kadar pasif kalır.'}</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Vazgeç</Button>
          <Button onClick={save} disabled={saving} data-testid="passive-until-save-btn">Süreyi Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
